import axios from 'axios';



// Axios instance with base URL already configured

const apiClient = axios.create({

  baseURL: process.env.REACT_APP_API_BASE || '/api',

  timeout: 30000,

  headers: { 'Content-Type': 'application/json' },

});



export interface IntegrityData {

  total: number;

  green: number;

  yellow: number;

  orange: number;

  red: number;

  greenPct: number;

  meters: Array<{

    id: string;

    name: string;

    status: 'green' | 'yellow' | 'orange' | 'red';

    lastRead: string;

  }>;

}



export interface OverviewData {

  pv: {

    total: number;

    breakdown: Record<string, number>; // meterId -> value

  };

  gridSupply: number;

  batteryDischarge: number;

  gridExport: number;

  batteryCharge: number;

  loadTotal: number;

  batterySoc: number;

}



export interface BatterySocData {

  currentSoc: number;

  previousSoc: number;

  delta: number;

  timestamp: string;

}



// Dashboard APIs

export const getDashboardIntegrity = async (cycle: number = 15): Promise<IntegrityData> => {

  const response = await apiClient.get('/meters/dashboard/integrity', { params: { cycle } });

  return response.data.data;

};



export const getDashboardOverview = async (

  mode: 'energy' | 'power',

  startTime?: string,

  endTime?: string,

): Promise<OverviewData> => {

  const params: Record<string, string> = { mode };

  if (startTime) params.startTime = startTime;

  if (endTime) params.endTime = endTime;

  const response = await apiClient.get('/meters/dashboard/overview', { params });

  return response.data.data;

};



export const getBatterySoc = async (): Promise<BatterySocData> => {

  const response = await apiClient.get('/meters/battery/soc');

  return response.data.data;

};



export { apiClient };

export default apiClient;


export interface MeterInfo {
  id: number;
  meter_number: number;
  meter_name: string;
  parent_meter_number: number;
  ratio: number;
  type_name: string;
  type_code: number;
}

export interface UsageMetrics {
  总用电量: {
    total: number;
    工业用电: number;
    居民用电: number;
    breakdown: Array<{
      meterNumber: number;
      usage: number;
      type?: string;
      note?: string;
    }>;
    failed?: Array<{
      meterNumber: number;
      error: string;
    }>;
    timestamp: string;
    error?: string;
  };
  总发电量: {
    total: number;
    breakdown: Array<{
      meterNumber: number;
      generation: number;
    }>;
    failed?: Array<{
      meterNumber: number;
      error: string;
    }>;
    timestamp: string;
    error?: string;
  };
  购电量: {
    total: number;
    meterNumber: number;
    actualUsage: number;
    actualHours: number;
    timestamp: string;
    error?: string;
  };
  售电量: {
    total: number;
    meterNumber: number;
    actualSale: number;
    actualHours: number;
    timestamp: string;
    error?: string;
  };
  调度电量: {
    total: number;
    meterNumber: number;
    actualDispatch: number;
    actualHours: number;
    timestamp: string;
    error?: string;
  };
  timestamp: string;
}

export interface QueryParams {
  meterNumbers: number[];
  startDate: string;
  endDate: string;
  dataType?: 'energy' | 'power';
}

export interface QueryResult {
  查询时间范围: string;
  数据类型: string;
  结果: Array<{
    电表号: number;
    电表类型名称: string;
    电表名称: string;
    开始读数?: number;
    结束读数?: number;
    用电量kWh?: number;
    倒送电量kWh?: number;
    查询时间?: string;
    有功功率kW?: number;
    无功功率kVar?: number;
    视在功率kVA?: number;
    功率因数?: number;
    错误?: string;
  }>;
}

export const meterService = {
  // 获取所有电表信息
  async getAllMeters(): Promise<MeterInfo[]> {
    try {
      const response = await apiClient.get('/meters');
      return response.data.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.error || '获取电表信息失败');
    }
  },

  // 获取电表层级结构
  async getMeterHierarchy(): Promise<Record<string, MeterInfo[]>> {
    try {
      const response = await apiClient.get('/meters/hierarchy');
      return response.data.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.error || '获取电表层级结构失败');
    }
  },

  // 根据父级获取子电表
  async getMetersByParent(parentId: number): Promise<MeterInfo[]> {
    try {
      const response = await apiClient.get(`/meters/parent/${parentId}`);
      return response.data.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.error || '获取子电表信息失败');
    }
  },

  // 获取特定电表信息
  async getMeterById(meterNumber: number): Promise<MeterInfo> {
    try {
      const response = await apiClient.get(`/meters/${meterNumber}`);
      return response.data.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.error || '获取电表信息失败');
    }
  },

  // 获取综合用电量数据（用电量 Tab 的5个指标）
  async getUsageMetrics(timeRange?: { startTime: string; endTime: string }): Promise<UsageMetrics> {
    try {
      const params = timeRange ? timeRange : {};
      const response = await apiClient.get('/meters/dashboard/usage', { params });
      return response.data.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.error || '获取用电量数据失败');
    }
  },

  // 获取总用电量详细数据
  async getTotalConsumptionDetail(endTime?: string): Promise<any> {
    try {
      const params = endTime ? { endTime } : {};
      const response = await apiClient.get('/meters/dashboard/usage/total-consumption', { params });
      return response.data.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.error || '获取总用电量详细数据失败');
    }
  },

  // 获取总发电量详细数据
  async getTotalGenerationDetail(endTime?: string): Promise<any> {
    try {
      const params = endTime ? { endTime } : {};
      const response = await apiClient.get('/meters/dashboard/usage/total-generation', { params });
      return response.data.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.error || '获取总发电量详细数据失败');
    }
  },

  // 获取电表24小时用电量
  async getMeter24HourUsage(meterNumber: number, endTime?: string): Promise<any> {
    try {
      const params = endTime ? { endTime } : {};
      const response = await apiClient.get(`/meters/${meterNumber}/usage/24h`, { params });
      return response.data.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.error || '获取24小时用电量失败');
    }
  },

  // 获取电表小时级用电量数据
  async getMeterHourlyUsage(meterNumber: number, date?: string): Promise<any> {
    try {
      const params = date ? { date } : {};
      const response = await apiClient.get(`/meters/${meterNumber}/usage/hourly`, { params });
      return response.data.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.error || '获取小时级用电量失败');
    }
  },

  // 获取光伏发电电表信息
  async getPVMeters(): Promise<MeterInfo[]> {
    try {
      const response = await apiClient.get('/meters/types/pv');
      return response.data.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.error || '获取光伏电表信息失败');
    }
  },

  // 查询能耗数据
  async queryEnergyData(params: QueryParams): Promise<QueryResult> {
    try {
      const response = await apiClient.post('/query/energy', params);
      return response.data.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.error || '查询能耗数据失败');
    }
  },

  // 导出查询结果为 CSV
  async exportCSV(params: QueryParams): Promise<Blob> {
    try {
      const response = await apiClient.post('/query/export/csv', params, {
        responseType: 'blob',
      });
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.error || '导出CSV失败');
    }
  },
};